import { useEffect, useMemo, useState } from 'react';
import { MetricGridView } from '@/components/views/MetricGridView';
import { DataTableView } from '@/components/views/DataTableView';
import { ChartView } from '@/components/views/chart/ChartView';
import { CHART_MAX_SERIES, type ChartViewData, type DataTableCellTone, type DataTableViewData } from '@shared/types/views';
import { type AppCollection, type AppMember, type AppRecord, loadAppList, loadAppMembers } from '@/lib/app-records';
import { type AppReview, loadAppReviews, SCORE_MAX, teamSummary } from '@/lib/app-reviews';

/** Reading of an average score: below « évoqué » is weak, below « qualifié » is to work on. */
const toneOf = (value: number | null): DataTableCellTone | undefined =>
  value === null ? undefined : value < 1 ? 'critical' : value < 2 ? 'warning' : 'good';

const format = (value: number | null) => value === null ? null : `${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} / ${SCORE_MAX}`;

/** F5c, for the manager: per person and per criterion, from the human reviews. Drawn with Domino's own views. */
export const AppTeamReview = ({ appId }: { appId: string }) => {
  const [data, setData] = useState<{ collection: AppCollection; records: AppRecord[]; reviews: AppReview[]; members: AppMember[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([loadAppList(appId), loadAppReviews(appId), loadAppMembers()])
      .then(([list, reviews, members]) => { if (list) setData({ ...list, reviews, members }); })
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Vue d’équipe indisponible.'));
  }, [appId]);

  const views = useMemo(() => {
    const grid = data?.collection.reviewGrid;
    if (!data || !grid) return null;
    const rows = teamSummary(data.records, data.reviews, grid, data.members);
    const reviewed = rows.reduce((sum, row) => sum + row.reviewed, 0);
    const objects = rows.reduce((sum, row) => sum + row.objects, 0);
    const scored = rows.filter((row) => row.overall !== null);
    const overall = scored.length ? scored.reduce((sum, row) => sum + row.overall!, 0) / scored.length : null;
    const band = {
      presentation: 'band' as const,
      metrics: [
        { label: 'Appels', value: objects },
        { label: 'Revus', value: `${reviewed} sur ${objects}`, status: reviewed < objects ? 'warning' as const : 'good' as const },
        { label: 'Note moyenne', value: format(overall) ?? '—' },
      ],
    };
    const table: DataTableViewData = {
      title: 'Par personne',
      columns: [
        { key: 'name', label: 'Personne' }, { key: 'reviewed', label: 'Revus' },
        ...grid.map((criterion) => ({ key: criterion.key, label: criterion.label })), { key: 'weakest', label: 'À travailler' },
      ],
      rows: rows.map((row) => ({
        id: row.ownerUserId,
        cells: [
          { key: 'name', value: row.name }, { key: 'reviewed', value: `${row.reviewed} sur ${row.objects}` },
          ...grid.map((criterion) => ({ key: criterion.key, value: format(row.averages[criterion.key] ?? null), tone: toneOf(row.averages[criterion.key] ?? null) })),
          { key: 'weakest', value: row.weakest?.label ?? null },
        ],
      })),
      empty_message: 'Aucun appel revu pour l’instant.',
    };
    const chart: ChartViewData = {
      title: 'Moyenne par critère', kind: 'bar', unit: `/${SCORE_MAX}`,
      categories: grid.map((criterion) => criterion.label),
      series: scored.slice(0, CHART_MAX_SERIES).map((row) => ({ key: row.ownerUserId, label: row.name, values: grid.map((criterion) => row.averages[criterion.key] ?? null) })),
      insight: rows.every((row) => !row.weakest) ? undefined
        : `Critère le plus faible de l’équipe : ${grid.map((criterion) => ({ criterion, value: scored.reduce((sum, row) => sum + (row.averages[criterion.key] ?? 0), 0) }))
          .sort((left, right) => left.value - right.value)[0]!.criterion.label}.`,
    };
    return { band, table, chart };
  }, [data]);

  if (error) return <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>;
  if (data && !data.collection.reviewGrid) return <p className="text-sm text-domino-inkSoft">Pas encore de grille de revue : un manager l’ajoute depuis la liste.</p>;
  if (!views) return <p role="status" className="text-sm text-domino-inkSoft">Chargement de la vue d’équipe…</p>;
  return (
    <div className="space-y-6">
      <MetricGridView data={views.band} hideTitle />
      <div className="view-container"><DataTableView data={views.table} /></div>
      {views.chart.series.length > 0 && <div className="view-container rounded-2xl border border-domino-cardBorder bg-domino-white p-4"><ChartView data={views.chart} /></div>}
      <p className="text-xs text-domino-inkSoft">Calculé sur la dernière revue de chaque appel. 0 Absent · 1 Évoqué · 2 Qualifié · 3 Validé.</p>
    </div>
  );
};
